import React, { useCallback, useEffect, useMemo, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import { useTheme } from "@mui/styles";
import { IFlight, IGroundEvent, IFlightCrewAcceptanceRequestResult } from "@vmosolutions/oc-interfaces";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Grid,
} from "@mui/material";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { DialogModalStyles, EventModalTypes } from "../DialogModalWrapper";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import moment from "moment-timezone";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import chroma from "chroma-js";

interface IAssignFlightsToPairingModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

export const ReleaseFlightModal = (props: IAssignFlightsToPairingModalProps): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal } = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const { cancellablePromise } = useCancellablePromise();

  const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);
  const flightReleaseAutoSelectContent = useSelector(
    (state: IRootState) => state.UISettings.flightReleaseAutoSelectContent
  );
  const [flightReleaseState, setFlightReleaseState] = useState<IFlightCrewAcceptanceRequestResult | null>();
  const [contentIDs, setContentIDs] = useState<string[]>([]);
  const [loadingContent, setLoadingContent] = useState<boolean>(true);
  const [contentLoaded, setContentLoaded] = useState<boolean>(false);
  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const oneFlightSelected: boolean = selectedFlights.length === 1 ? true : false;
  const isReleased = selectedFlights[0]?.isReleased;

  const handleCancel = useCallback((): void => {
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  }, [handleClose, setActiveEventModal]);

  const getFlightReleaseState = useCallback(async (): Promise<void> => {
    const res = await AmplifyAPIWrapper.Get<any>(
      `/flights/release/state/${selectedFlights[0]?._id}`,
      undefined,
      cancellablePromise
    );
    if (res && res.hasOwnProperty("_id")) {
      setFlightReleaseState(res);

      //add efb flights to list, so they are disabled and checked in form.
      const efbContentIDs: string[] = [];
      res.contents.map((content: any) => {
        if (content.hasOwnProperty("efb")) {
          efbContentIDs.push(content._id);
        }
      });
      setContentIDs(efbContentIDs);
    }
    setLoadingContent(false);
    setContentLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateContentIDs = (contentID: string, action: "add" | "remove"): void => {
    const newContentIDs = [...contentIDs];
    const contentIdsIdx = newContentIDs.findIndex((content) => content === contentID);
    if (action === "add" && contentIdsIdx === -1) {
      newContentIDs.push(contentID);
    }
    if (action === "remove" && contentIdsIdx !== -1) {
      newContentIDs.splice(contentIdsIdx, 1);
    }
    setContentIDs(newContentIDs);
  };

  const allReleaseOnlyAllowMostRecent = useMemo(() => {
    if (flightReleaseState) {
      const allRecents = [
        ...new Set(flightReleaseState.contents.map((item) => item.contentType.releaseOnlyAllowMostRecent)),
      ];
      return !allRecents.includes(false);
    }
    return false;
  }, [flightReleaseState]);

  const uniqueContentTypes = useMemo(() => {
    if (flightReleaseState) {
      return [...new Set(flightReleaseState.contents.map((item) => item.contentType.title))];
    }
    return [];
  }, [flightReleaseState]);

  const selectNewContentByType = useCallback((): void => {
    const recentContentIds: string[] = [];
    if (!flightReleaseState) {
      return;
    }
    uniqueContentTypes.map((contentType, contentTypeKey) => {
      const nonefbFiles = flightReleaseState.contents
        .filter((content) => {
          return !content.hasOwnProperty("efb") && content.contentType.title === contentType;
        })
        .sort((a, b) => {
          return a.displayTitle > b.displayTitle ? -1 : 1;
        });
      if (nonefbFiles && nonefbFiles.length > 0) {
        recentContentIds.push(nonefbFiles[0]._id);
      }
    });
    //re-add pre-checked content.
    flightReleaseState.contents.map((content: any) => {
      if (content.hasOwnProperty("efb")) {
        recentContentIds.push(content._id);
      }
    });

    setContentIDs(recentContentIds);
  }, [flightReleaseState, uniqueContentTypes]);

  /** Dispatch update flight call sending only isReleased boolean as true and nothing else. */
  const handleReleaseFlight = useCallback(async (): Promise<void> => {
    const flightId = selectedFlights[0]?._id;
    if (!flightId) return;
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${isReleased ? "Re-Releasing" : "Releasing"} selected flight...`,
      successMessage: `Selected flight ${isReleased ? "Re-Released" : "Released"} Successfully`,
      errorMessage: `Error: Unable to ${isReleased ? "Re-Release" : "Release"} selected flight`,
    };
    await AmplifyAPIWrapper.Post<any>(
      `/flights/release/${flightId}`,
      contentIDs, // Nothing else should be included in this payload.
      notifications
    );
    // After initiated call to release flight, close modal and reset active modal status.
    handleCancel();
  }, [selectedFlights, dispatch, isReleased, contentIDs, handleCancel]);

  useEffect(() => {
    getFlightReleaseState();
  }, [getFlightReleaseState]);

  useEffect(() => {
    if (contentLoaded && flightReleaseAutoSelectContent === true) {
      selectNewContentByType();
    }
  }, [contentLoaded, flightReleaseAutoSelectContent, selectNewContentByType]);

  if (isOpen && oneFlightSelected) {
    return (
      <DialogContent sx={(theme) => DialogModalStyles(theme)}>
        <div className="modal_content" style={{ padding: 10 }}>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={"auto"}>
              <FlightChipCollection flights={selectedFlights} />
            </Grid>
            <Grid item xs>
              <Box
                sx={(theme) => ({
                  display: "flex",
                  fontWeight: 900,
                  color: theme.palette.common.white,
                  alignItems: "center",
                  px: 2,
                  fontSize: "1.3em",
                })}
              >
                {isReleased ? "Re-Release" : "Release"} Flight With Selected Content
              </Box>
            </Grid>
            <Grid container item xs={12} sx={(theme) => ({ color: theme.palette.common.white })}>
              {loadingContent && (
                <Box sx={{ py: 6, px: 2, display: "flex", justifyContent: "center", width: "100%" }}>
                  <LoadingWrapper message="Loading Available Content..." />
                </Box>
              )}
              {flightReleaseState && !loadingContent && uniqueContentTypes.length === 0 && (
                <Box sx={{ p: 3, width: "100%", textAlign: "center" }}>No content found for this flight.</Box>
              )}
              {flightReleaseState &&
                !loadingContent &&
                uniqueContentTypes.map((contentType, contentTypeKey) => {
                  const contentList = flightReleaseState.contents.filter((content) => {
                    return content.contentType.title === contentType;
                  });
                  const efbFiles = flightReleaseState.contents
                    .filter((content) => {
                      return content.hasOwnProperty("efb") && content.contentType.title === contentType;
                    })
                    .sort((a, b) => {
                      return a.efb.madeAvailAt > b.efb.madeAvailAt ? -1 : 1;
                    });
                  return (
                    <Accordion
                      defaultExpanded={true}
                      key={contentTypeKey}
                      sx={{ width: "100%", m: 0, p: 0, "&.Mui-expanded": { marginTop: 0, marginBottom: 0 } }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${contentTypeKey}-content`}
                        id={`panel${contentTypeKey}-header`}
                        sx={(theme) => ({
                          "& .MuiAccordionSummary-content": {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          },
                          backgroundColor: chroma(theme.palette.background.paper).darken(0.1).desaturate(0.5).css(),
                        })}
                      >
                        {contentType}
                        <Box sx={{ mr: 1 }}>
                          {efbFiles.length > 0 && (
                            <Chip
                              label={`${efbFiles[0].displayTitle}`}
                              color="success"
                              sx={(theme) => ({
                                "& .MuiChip-label": { py: 0, px: 0.25 },
                                p: 1,
                                height: "18px",
                                borderRadius: "3px",
                                backgroundColor: chroma(theme.palette.success.dark).darken(0.2).css(),
                              })}
                            />
                          )}
                          {efbFiles.length > 1 && (
                            <Chip
                              label={`+ ${efbFiles.length - 1} file${efbFiles.length > 2 ? "s" : ""}`}
                              color="info"
                              sx={{
                                "& .MuiChip-label": { py: 0, px: 0.25 },
                                p: 1,
                                ml: 0.5,
                                height: "18px",
                                borderRadius: "3px",
                              }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      {contentList
                        .sort((a: any, b: any) => {
                          return a.contentType.title >= b.contentType.title ? -1 : 1;
                        })
                        .map((content: any, key: number) => {
                          const isIncluded = contentIDs.includes(content._id);
                          const isDisabled =
                            content.hasOwnProperty("efb") || content.contentType.releaseOnlyAllowMostRecent === true;
                          return (
                            <AccordionDetails sx={{ p: 0 }} key={key}>
                              <Grid
                                key={key}
                                item
                                xs={12}
                                sx={(theme) => ({
                                  cursor: "pointer",
                                  padding: "1px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  "&:hover": {
                                    backgroundColor: theme.palette.primary.dark,
                                  },
                                })}
                                onClick={() => {
                                  if (!content.hasOwnProperty("efb") && !isDisabled) {
                                    updateContentIDs(content._id, isIncluded ? "remove" : "add");
                                  }
                                }}
                              >
                                <Box>
                                  <Checkbox
                                    checked={isIncluded}
                                    disabled={isDisabled}
                                    sx={(theme) => ({
                                      "&.Mui-checked": {
                                        color: theme.palette.common.white,
                                        fill: theme.palette.common.white,
                                        "&.Mui-disabled": {
                                          color: theme.palette.grey[600],
                                          fill: theme.palette.grey[600],
                                        },
                                      },
                                    })}
                                    onClick={() => {
                                      if (!content.hasOwnProperty("efb") && !isDisabled) {
                                        updateContentIDs(content._id, isIncluded ? "remove" : "add");
                                      }
                                    }}
                                  />
                                  {content.displayTitle}
                                </Box>
                                {content.hasOwnProperty("efb") && (
                                  <Box
                                    sx={(theme) => ({
                                      fontSize: "0.8em",
                                      justifySelf: "end",
                                      color: theme.palette.grey[400],
                                      pr: "4px",
                                    })}
                                  >
                                    (Made Available At:{" "}
                                    {moment(content.efb.madeAvailAt).utc().format("MMM DD, YYYY HH:mm")} UTC)
                                  </Box>
                                )}
                              </Grid>
                            </AccordionDetails>
                          );
                        })}
                    </Accordion>
                  );
                })}
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: allReleaseOnlyAllowMostRecent || loadingContent ? "end" : "space-between",
              }}
            >
              {allReleaseOnlyAllowMostRecent !== true && !loadingContent && (
                <Button
                  color="info"
                  size="small"
                  variant="contained"
                  onClick={() => {
                    selectNewContentByType();
                  }}
                >
                  Select recent for all types
                </Button>
              )}
              <ButtonGroup variant="contained" size="small">
                <Button
                  color="error"
                  onClick={() => {
                    handleCancel();
                  }}
                >
                  Cancel
                </Button>
                {contentLoaded && (
                  <Button
                    color="success"
                    onClick={() => {
                      handleReleaseFlight();
                    }}
                  >
                    {isReleased ? "Re-Release" : "Release"} Flight
                  </Button>
                )}
              </ButtonGroup>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
